/**
 * /family — Family Trees directory.
 *
 * Everyone (signed in or not) can see the cards of every tree.
 * Access to the tree's members/photos is gated inside the detail page
 * per the tree's `visibility` field.
 *
 * A prominent Celebrate a Life callout sits at the top so families
 * discover the memorials feature naturally when researching lineage.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { TreeDeciduous, Plus, Users, Globe, Lock, MailPlus, Flower2, ArrowUpRight, Sparkles, X } from "lucide-react";
import { paletteFor } from "@/lib/palette";
import TranslatableInput from "@/components/TranslatableInput";
import AIImproveButton from "@/components/AIImproveButton";

const VIS_LABEL = { public: "Public", invite: "Invite-only", private: "Private" };
const VIS_ICON = { public: Globe, invite: MailPlus, private: Lock };

const FT_ILLUSTRATION = "https://customer-assets-39nsmqrw.emergentagent.net/job_kindness-hub-35/artifacts/pkc523yi_family-tree-home-graphics-2.jpg";

export default function FamilyTreesList() {
  const { user, loading: authLoading } = useAuth();
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    // Each call is independent — a failure on one shouldn't hide the other.
    if (user) {
      try {
        const { data: m } = await http.get("/family-trees", { params: { scope: "mine" } });
        setMine(m.items || []);
      } catch { /* silent — user might be transiently unauth'd */ }
    } else {
      setMine([]);
    }
    // Retry the public directory call once on transient failure (network flap
    // or in-flight auth token). Silently ignore axios cancellation errors.
    const fetchAll = async (attempt = 0) => {
      try {
        const { data: p } = await http.get("/family-trees", { params: { scope: "all" } });
        setAll(p.items || []);
        return true;
      } catch (e) {
        if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return true; // benign
        if (attempt === 0) {
          await new Promise((r) => setTimeout(r, 600));
          return fetchAll(1);
        }
        return false;
      }
    };
    const ok = await fetchAll(0);
    if (!ok) {
      toast.error("Couldn't load the family tree directory. Please retry in a moment.");
    }
    setLoading(false);
  };

  // Wait until AuthContext has finished resolving before firing — this
  // avoids a race where the very first render fires with user=null and a
  // stale in-flight auth token, then fires again once auth resolves.
  useEffect(() => {
    if (authLoading) return;
    load();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [user, authLoading]);

  const filtered = q.trim()
    ? all.filter((t) => {
        const hay = [t.name, t.description, t.country, ...(t.alternate_names || []), ...(t.branch_names || [])]
          .filter(Boolean).join(" ").toLowerCase();
        return hay.includes(q.trim().toLowerCase());
      })
    : all;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="family-list-page">
      {/* Marketing / product overview link — top of page */}
      <Link
        to="/family-and-memorials"
        className="group flex flex-wrap items-center justify-between gap-3 rounded-2xl border-2 border-dashed px-5 py-3 mb-6 hover:bg-white transition-colors"
        style={{ borderColor: "#B25D3844", background: "#FDFBF7" }}
        data-testid="family-marketing-link"
      >
        <div className="flex items-center gap-3">
          <span className="text-lg">🌳</span>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em]" style={{ color: "#B25D38" }}>
              New here?
            </div>
            <div className="text-sm font-medium" style={{ color: "#2D2A26" }}>
              Family Trees &amp; Eternal Memorials &mdash; <span className="opacity-70">preserve your family legacy</span>
            </div>
          </div>
        </div>
        <span
          className="text-sm inline-flex items-center gap-1.5 font-medium group-hover:gap-2 transition-all"
          style={{ color: "#B25D38" }}
        >
          Read the full story <ArrowUpRight className="w-4 h-4" />
        </span>
      </Link>

      {/* Celebrate a Life callout — TOP boxed section (per user copy) */}
      <div
        className="rounded-3xl border-2 p-6 md:p-8 mb-10 relative overflow-hidden"
        style={{ borderColor: "#5B4A73", background: "linear-gradient(135deg, #F3E8FF 0%, #FFE4E6 55%, #FEF3C7 100%)" }}
        data-testid="family-top-celebrate-cta"
      >
        <div className="flex flex-wrap items-start justify-between gap-4 relative z-10">
          <div className="max-w-3xl">
            <div className="overline mb-2" style={{ color: "#5B4A73" }}>
              <Flower2 size={13} className="inline mr-1.5 -mt-0.5" /> CELEBRATE A LIFE · MEMORIALS
            </div>
            <h2 className="font-display text-2xl md:text-3xl leading-tight" style={{ color: "#3D2E5A" }}>
              Every branch of your family tree represents a life worth remembering.
            </h2>
            <p className="mt-3 text-sm md:text-base max-w-2xl" style={{ color: "#5B4A73" }}>
              Honor the memory of your parents, grandparents, ancestors, relatives, and dear friends by creating a beautiful tribute in <b>Celebrate a Life</b>. Share their life story, photographs, achievements, kind messages from loved ones and friends, values, and cherished memories so future generations will know not only who they were — but how they lived, loved, and inspired others.
            </p>
          </div>
          <Link
            to="/celebrate"
            className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium shadow-md hover:shadow-lg transition-shadow shrink-0"
            style={{ background: "#5B4A73", color: "white" }}
            data-testid="family-celebrate-open"
          >
            Open Celebrate a Life <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Hero row */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>
            <TreeDeciduous size={13} className="inline mr-1.5 -mt-0.5" /> GLOBAL FAMILY TREES
          </div>
          <h1 className="font-display text-4xl md:text-5xl leading-tight font-black" data-testid="family-page-caption">
            Every Family Has a Story
          </h1>
          <p className="mt-3 font-display text-lg md:text-xl" style={{ color: "var(--wofy-text-soft)" }}>
            Trace your lineage — together.
          </p>
          <p className="mt-3 text-sm max-w-2xl" style={{ color: "var(--wofy-text-soft)" }}>
            Build a family tree, invite relatives across the world to contribute, log births / weddings /
            anniversaries, and connect deceased ancestors to Celebrate a Life memorials.
          </p>
        </div>
        {user && (
          <button onClick={() => setCreating(true)} data-testid="family-new" className="wofy-btn-primary">
            <Plus className="w-4 h-4" /> Create Your Family Tree
          </button>
        )}
        <Link to="/family-tree-guide" data-testid="family-guide-link" className="wofy-btn-ghost">
          How it works →
        </Link>
      </div>

      {/* Illustration */}
      <div className="mb-8 rounded-3xl overflow-hidden border" style={{ borderColor: "var(--wofy-border)" }} data-testid="family-tree-illustration">
        <img
          src={FT_ILLUSTRATION}
          alt="Our Family, Our Story — a family tree illustration"
          className="w-full h-auto object-cover"
          loading="lazy"
        />
      </div>

      {user && (
        <Link
          to="/me?tab=family"
          data-testid="family-digest-cta"
          className="mb-8 flex items-center justify-between gap-4 rounded-2xl border p-5 transition-shadow hover:shadow-md"
          style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
        >
          <div>
            <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>NEW · WEEKLY DIGEST</div>
            <div className="font-display text-lg leading-snug">Get a weekly family anniversary email</div>
            <div className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
              Birthdays, wedding anniversaries and remembrance days across every tree you belong to.
            </div>
          </div>
          <ArrowUpRight className="w-5 h-5 shrink-0" style={{ color: "var(--wofy-primary)" }} />
        </Link>
      )}

      {/* My trees */}
      {user && mine.length > 0 && (
        <section className="mb-10">
          <h2 className="font-display text-2xl mb-4">Your trees</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">{mine.map((t) => <TreeCard key={t.id} t={t} />)}</div>
        </section>
      )}

      {/* All trees — every card visible to every visitor */}
      <section>
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <h2 className="font-display text-2xl">All family trees {all.length > 0 && <span className="text-base opacity-60">· {all.length}</span>}</h2>
          <div className="relative">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, alternate names, branch, country…"
              className="wofy-input !py-1.5 !text-sm w-72 max-w-full"
              data-testid="family-search"
            />
            {q && (
              <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100" aria-label="Clear">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="family-tree-skeleton">
            {Array.from({ length: 6 }).map((_, i) => <TreeCardSkeleton key={i} delay={i * 60} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 rounded-2xl border" style={{ borderColor: "#E8DFD1", background: "#FDFBF7" }}>
            <TreeDeciduous className="w-10 h-10 mx-auto mb-3" style={{ color: "#D4C4AF" }} />
            <p className="font-display text-lg" style={{ color: "#2D2A26" }}>
              {q.trim() ? "No trees match your search." : "This corner of Social Pulse is still a seed."}
            </p>
            <p className="text-sm mt-1" style={{ color: "#5C574F" }}>
              {q.trim() ? "Try a different name or place." : "Plant the first family tree and let it grow across generations."}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">{filtered.map((t) => <TreeCard key={t.id} t={t} />)}</div>
        )}
      </section>

      {creating && <NewTreeModal onClose={() => setCreating(false)} onCreated={load} />}
    </div>
  );
}

// A hand-picked palette of gentle "family album" gradients. Shared with
// /celebrate, /causes and /pulse via `lib/palette.js`. Local list kept
// only for reference — actual palettes come from paletteFor().
const CARD_PALETTES_UNUSED = [];

function TreeCard({ t }) {
  const Icon = VIS_ICON[t.visibility] || Globe;
  const alts = (t.alternate_names || []).slice(0, 3);
  const branches = (t.branch_names || []).slice(0, 3);
  const palette = paletteFor(t.slug || t.id || t.name);
  return (
    <Link
      to={`/family/${t.slug || t.id}`}
      data-testid={`family-card-${t.id}`}
      className="group relative block rounded-2xl overflow-hidden border transition-all hover:-translate-y-0.5 hover:shadow-lg"
      style={{ borderColor: `${palette.accent}33`, background: `linear-gradient(135deg, ${palette.from} 0%, ${palette.to} 100%)` }}
    >
      {/* Tree silhouette watermark — pure SVG, deterministic ink from palette */}
      <TreeMotif ink={palette.ink} />

      {(t.banner_url || t.cover_image) ? (
        <img src={t.banner_url || t.cover_image} alt="" className="w-full h-32 object-cover" loading="lazy" />
      ) : null}

      <div className="relative p-5">
        {/* Top row: visibility pill + country */}
        <div className="flex items-center justify-between gap-2 mb-3">
          <span
            className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full"
            style={{ background: `${palette.accent}18`, color: palette.ink }}
          >
            <Icon className="w-3 h-3" /> {VIS_LABEL[t.visibility] || t.visibility}
          </span>
          {t.country && (
            <span className="text-[10px] font-mono uppercase tracking-widest opacity-70" style={{ color: palette.ink }}>
              {t.country}
            </span>
          )}
        </div>

        <h3 className="font-display text-xl leading-tight" style={{ color: palette.ink }}>{t.name}</h3>

        {(alts.length > 0 || branches.length > 0) && (
          <div className="mt-2 flex flex-wrap gap-1">
            {alts.map((n) => (
              <span
                key={"a-" + n}
                className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-white/60"
                style={{ color: palette.ink }}
              >alt · {n}</span>
            ))}
            {branches.map((n) => (
              <span
                key={"b-" + n}
                className="text-[10px] font-mono px-1.5 py-0.5 rounded-full"
                style={{ background: palette.accent, color: "#fff" }}
              >branch · {n}</span>
            ))}
          </div>
        )}

        {t.description && (
          <p className="text-sm mt-3 line-clamp-2 opacity-85" style={{ color: palette.ink }}>{t.description}</p>
        )}

        <div className="mt-4 pt-3 flex items-center justify-between text-xs" style={{ borderTop: `1px dashed ${palette.accent}55`, color: palette.ink }}>
          <span className="inline-flex items-center gap-1">
            <Users className="w-3.5 h-3.5" /> {t.member_count || 0} member{t.member_count === 1 ? "" : "s"}
          </span>
          <span
            className="inline-flex items-center gap-1 font-medium opacity-80 group-hover:opacity-100 transition-opacity"
          >
            Open tree <ArrowUpRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}

/**
 * Subtle background silhouette of a tree (trunk + canopy) drawn at ~10%
 * opacity in the bottom-right of each card. Gives every card an organic
 * feel without needing an image asset.
 */
function TreeMotif({ ink }) {
  return (
    <svg
      className="absolute -bottom-6 -right-4 w-40 h-40 pointer-events-none select-none"
      viewBox="0 0 100 100"
      aria-hidden
    >
      <g fill={ink} opacity="0.09">
        <circle cx="50" cy="35" r="22" />
        <circle cx="32" cy="45" r="16" />
        <circle cx="68" cy="45" r="16" />
        <circle cx="50" cy="55" r="14" />
        <rect x="46" y="55" width="8" height="34" rx="2" />
      </g>
    </svg>
  );
}

/**
 * Grey/pulsing placeholder card shown while the directory loads. Uses
 * `animation-delay` inline to stagger the pulse so the whole grid feels
 * alive on cold refresh instead of hitting all at once.
 */
function TreeCardSkeleton({ delay = 0 }) {
  return (
    <div
      className="rounded-2xl border overflow-hidden animate-pulse"
      style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)", animationDelay: `${delay}ms` }}
      aria-hidden
    >
      <div className="h-24" style={{ background: "linear-gradient(90deg,#E5E7EB,#F3F4F6,#E5E7EB)" }} />
      <div className="p-5 space-y-3">
        <div className="h-3 w-24 rounded-full bg-gray-200" />
        <div className="h-5 w-3/4 rounded bg-gray-300" />
        <div className="flex gap-1">
          <div className="h-3 w-14 rounded-full bg-gray-200" />
          <div className="h-3 w-16 rounded-full bg-gray-200" />
        </div>
        <div className="h-3 w-full rounded bg-gray-200" />
        <div className="h-3 w-5/6 rounded bg-gray-200" />
        <div className="mt-4 pt-3 border-t flex items-center justify-between" style={{ borderColor: "var(--wofy-border)" }}>
          <div className="h-3 w-20 rounded-full bg-gray-200" />
          <div className="h-3 w-16 rounded-full bg-gray-200" />
        </div>
      </div>
    </div>
  );
}

function NewTreeModal({ onClose, onCreated }) {
  const nav = useNavigate();
  const DRAFT_KEY = "ft-draft-newtree-v1";
  const initial = (() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return null;
  })();
  const [f, setF] = useState(initial || {
    name: "", country: "", language: "en", description: "", visibility: "invite",
    alternate_names: [""],
    branch_names: [""],
  });
  const hasDraft = !!initial;
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const updAlt = (i, v) => setF((p) => ({ ...p, alternate_names: p.alternate_names.map((x, idx) => idx === i ? v : x) }));
  const updBr = (i, v) => setF((p) => ({ ...p, branch_names: p.branch_names.map((x, idx) => idx === i ? v : x) }));

  // Persist draft so accidentally closing / navigating doesn't lose input.
  useEffect(() => {
    try {
      const hasContent = (f.name || "").trim() || (f.description || "").trim();
      if (hasContent) localStorage.setItem(DRAFT_KEY, JSON.stringify(f));
    } catch { /* ignore quota */ }
  }, [f]);

  const clearDraft = () => { try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ } };

  // Helpers so TranslatableInput values (str or dict) survive submit.
  const pickBase = (v) => {
    if (!v) return "";
    if (typeof v === "string") return v;
    if (typeof v === "object") return v[v.__base || "en"] || "";
    return String(v);
  };

  const submit = async (e) => {
    e.preventDefault();
    const nameStr = pickBase(f.name).trim();
    if (nameStr.length < 2) return toast.error("Please enter a tree name");
    setBusy(true);
    try {
      const payload = {
        ...f,
        // Backend expects strings for `name` and `description`; send base language.
        name: nameStr,
        description: pickBase(f.description),
        alternate_names: f.alternate_names.map((n) => n.trim()).filter(Boolean),
        branch_names: f.branch_names.map((n) => n.trim()).filter(Boolean),
      };
      const { data } = await http.post("/family-trees", payload);
      toast.success(`"${data.name}" created`);
      clearDraft();
      onClose();
      onCreated?.();
      nav(`/family/${data.slug || data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create tree");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className="fixed inset-0 z-[70] bg-[#14213D]/50 backdrop-blur-sm flex items-start justify-center p-4 pt-28 overflow-y-auto"
      onClick={onClose}
    >
      <div className="wofy-card w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="new-tree-modal">
        <div className="overline mb-2">CREATE FAMILY TREE</div>
        <h3 className="font-display text-2xl mb-1">Name your tree</h3>
        {hasDraft && (
          <div className="mb-3 text-[11px] flex items-center justify-between gap-2 px-2 py-1 rounded border" style={{ borderColor: "var(--wofy-border)", background: "rgba(139,92,246,0.06)" }}>
            <span>Draft restored — pick up where you left off.</span>
            <button
              type="button"
              onClick={() => {
                clearDraft();
                setF({ name: "", country: "", language: "en", description: "", visibility: "invite", alternate_names: [""], branch_names: [""] });
              }}
              className="underline opacity-80 hover:opacity-100"
              data-testid="tree-clear-draft"
            >Clear draft</button>
          </div>
        )}
        <form onSubmit={submit} className="space-y-3">
          <TranslatableInput
            label="Tree Name"
            as="input"
            testid="tree-name"
            placeholder="Tree name (e.g. The Nakamura Family)"
            value={f.name}
            onChange={(v) => upd("name", v)}
            required
          />
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="tree-country" className="wofy-input" placeholder="Country" value={f.country} onChange={(e) => upd("country", e.target.value)} />
            <input className="wofy-input" placeholder="Language code (en, es, hi…)" value={f.language} onChange={(e) => upd("language", e.target.value)} />
          </div>
          <div>
            <TranslatableInput
              label="Brief Description (optional)"
              as="textarea"
              rows={2}
              testid="tree-description"
              placeholder="Where the family is from, notable branches, etc."
              value={f.description}
              onChange={(v) => upd("description", v)}
            />
            {pickBase(f.description).trim().length >= 10 && (
              <div className="mt-1.5">
                <AIImproveButton
                  value={pickBase(f.description)}
                  onImproved={(t) => upd("description", t)}
                  kind="general"
                  testId="tree-desc-ai"
                />
              </div>
            )}
          </div>

          {/* Alternate family names */}
          <div>
            <div className="text-xs font-mono uppercase tracking-wider mb-1.5 flex items-center gap-2" style={{ color: "var(--wofy-text-soft)" }}>
              <Sparkles className="w-3 h-3" /> Alternate Family Names
              <span className="normal-case tracking-normal opacity-70">— for members who moved, adopted a different family name, or for adopted branches</span>
            </div>
            {f.alternate_names.map((n, i) => (
              <div key={i} className="flex gap-2 mb-1.5">
                <input
                  className="wofy-input flex-1 !py-1.5 !text-sm"
                  placeholder="e.g. de Silva, Fernandez, Kim"
                  value={n}
                  onChange={(e) => updAlt(i, e.target.value)}
                  data-testid={`tree-alt-${i}`}
                />
                {f.alternate_names.length > 1 && (
                  <button type="button" onClick={() => setF((p) => ({ ...p, alternate_names: p.alternate_names.filter((_, idx) => idx !== i) }))} className="text-xs opacity-60 hover:opacity-100" aria-label="Remove">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            {f.alternate_names.length < 10 && (
              <button type="button" onClick={() => setF((p) => ({ ...p, alternate_names: [...p.alternate_names, ""] }))} className="text-xs inline-flex items-center gap-1 opacity-70 hover:opacity-100" data-testid="tree-alt-add">
                <Plus className="w-3 h-3" /> Add another alternate name
              </button>
            )}
          </div>

          {/* Branch names */}
          <div>
            <div className="text-xs font-mono uppercase tracking-wider mb-1.5" style={{ color: "var(--wofy-text-soft)" }}>
              Branch Names <span className="normal-case tracking-normal opacity-70">— e.g. "The Kerala branch", "Adopted line", "In-laws"</span>
            </div>
            {f.branch_names.map((n, i) => (
              <div key={i} className="flex gap-2 mb-1.5">
                <input
                  className="wofy-input flex-1 !py-1.5 !text-sm"
                  placeholder="e.g. The Boston branch"
                  value={n}
                  onChange={(e) => updBr(i, e.target.value)}
                  data-testid={`tree-branch-${i}`}
                />
                {f.branch_names.length > 1 && (
                  <button type="button" onClick={() => setF((p) => ({ ...p, branch_names: p.branch_names.filter((_, idx) => idx !== i) }))} className="text-xs opacity-60 hover:opacity-100" aria-label="Remove">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            {f.branch_names.length < 10 && (
              <button type="button" onClick={() => setF((p) => ({ ...p, branch_names: [...p.branch_names, ""] }))} className="text-xs inline-flex items-center gap-1 opacity-70 hover:opacity-100" data-testid="tree-branch-add">
                <Plus className="w-3 h-3" /> Add another branch
              </button>
            )}
          </div>

          <label className="block text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
            Visibility
            <select data-testid="tree-visibility" className="wofy-input mt-1 normal-case tracking-normal" value={f.visibility} onChange={(e) => upd("visibility", e.target.value)}>
              <option value="invite">Invite-only (recommended)</option>
              <option value="public">Public (anyone can view)</option>
              <option value="private">Private (only admins & editors)</option>
            </select>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="wofy-btn-ghost">Cancel</button>
            <button type="submit" disabled={busy} data-testid="tree-create-submit" className="wofy-btn-primary disabled:opacity-60">
              {busy ? "Creating…" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
